import React, { Component } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import styled from 'styled-components';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';

import Form from './Form';
import FieldSet from './FieldSet';
import Input from './Input';
import RadioButtonGroup from './RadioButtonGroup';
import RadioButton from './RadioButton';
import Textarea from './Textarea';
import PrivacyPolicyCheckbox from './PrivacyPolicyCheckbox';
import { TextButton, LoadingButton } from '../Buttons';

const SuccessMessage = styled.p`
  text-align: center;
  font-weight: ${p => p.theme.weights.bold};
`;

const StyledComplaintsForm = styled.div`
  margin: 0 auto;
  max-width: ${p => p.theme.globalWidthSmall};
`;

const Row = styled.div`
  display: flex;
  margin: 0 -${p => p.theme.gutterXSmall};
`;

const Column = styled.div`
  flex: 1;
  margin: 0 ${p => p.theme.gutterXSmall} 30px ${p => p.theme.gutterXSmall};
`;

const Page = styled.div`
  font-size: 1rem;
  font-style: italic;
  margin-bottom: 20px;
`;

const BackButton = styled(TextButton)`
  display: block;
`;

const validation = step => {
  const stepsValidations = {
    1: {
      name: yup.string().required(),
      address: yup.string(),
      orgNumber: yup.string(),
      email: yup
        .string()
        .email()
        .required(),
      phone: yup.string().required(),
      invoiceNumber: yup.string().required(),
      cause: yup.string().required('Du må gjøre et valg.')
    },
    2: {
      reason: yup.string().required(),
      where: yup.string().required('Du må gjøre et valg.'),
      measures: yup.string(),
      accepted: yup
        .bool()
        .oneOf([true], 'Du må godkjenne personvernerklæringen.')
    }
  };

  return stepsValidations[step];
};

const mapValuesToFields = values => {
  const fields = [
    {
      key: 'input_1',
      value: values.name
    },
    {
      key: 'input_2',
      value: values.address
    },
    {
      key: 'input_3',
      value: values.orgNumber
    },
    {
      key: 'input_4',
      value: values.email
    },
    {
      key: 'input_5',
      value: values.phone
    },
    {
      key: 'input_6',
      value: values.invoiceNumber
    },
    {
      key: 'input_7',
      value: values.cause
    },
    {
      key: 'input_8',
      value: values.reason
    },
    {
      key: 'input_9',
      value: values.where
    },
    {
      key: 'input_10',
      value: values.measures
    }
  ];

  return fields;
};

yup.setLocale({
  mixed: {
    required: 'Dette feltet er obligatorisk.'
  },
  string: {
    email: 'Du må skrive inn en gyldig e-postadresse.',
    url: 'Du må skrive inn en gyldig nettadresse.',
    max: 'Du kan maks skrive inn ${max} tegn.' // eslint-disable-line no-template-curly-in-string
  }
});

export default class ComplaintsForm extends Component {
  state = {
    step: 1,
    totalSteps: 2,
    success: false
  };

  render() {
    const { step, totalSteps, success } = this.state;
    const { formId } = this.props;

    if (success) {
      return (
        <StyledComplaintsForm>
          <SuccessMessage>
            Takk for din henvendelse! Vi vil ta kontakt om kort tid.
          </SuccessMessage>
        </StyledComplaintsForm>
      );
    }

    return (
      <StyledComplaintsForm>
        <Mutation
          mutation={gql`
            mutation submitForm($submission: GFFormSubmission $id: Int) {
              submission:postGFFormSubmission(
                id: $id
                submission: $submission
              ){
                is_valid
                page_number
                source_page_number
                confirmation_message
              }
            }
          `}
        >
          {(submit, { loading, error, data }) => <Formik
            initialValues={{
              name: '',
              address: '',
              orgNumber: '',
              email: '',
              phone: '',
              invoiceNumber: '',
              cause: '',
              reason: '',
              where: '',
              measures: '',
              accepted: false
            }}
            validationSchema={yup.object().shape(validation(step))}
            onSubmit={(values, { setSubmitting }) => {
              if (step === 1) {
                this.setState({ step: 2 });

                setSubmitting(false);

                return;
              }

              submit({
                variables: {
                  id: formId,
                  submission: {
                    fields: mapValuesToFields(values)
                  }
                }
              }).catch((error) => {
                console.error(error);

                setSubmitting(false);
              }).then((data) => {
                setSubmitting(false);
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting
            }) => <div>
                {(data && data.submission) && <div
                  dangerouslySetInnerHTML={{
                    __html: data.submission.confirmation_message
                  }}
                ></div>}
                {!(data && data.submission) && <Form onSubmit={handleSubmit}>
                  <Page>
                    Trinn {step} av {totalSteps}
                  </Page>
                  {error && <div>
                    {error.toString()}
                  </div>}

                  {step === 1 && (
                    <FieldSet disabled={isSubmitting}>
                      <Row>
                        <Column>
                          <Input
                            name="name"
                            label="Navn/Bedrift m/kontaktperson"
                            isRequired={true}
                            value={values.name}
                            error={errors.name}
                            touched={touched.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Column>
                      </Row>

                      <Row>
                        <Column>
                          <Input
                            name="address"
                            label="Sted/adresse"
                            value={values.address}
                            error={errors.address}
                            touched={touched.address}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Column>

                        <Column>
                          <Input
                            name="orgNumber"
                            label="Foretaksnummer"
                            value={values.orgNumber}
                            error={errors.orgNumber}
                            touched={touched.orgNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Column>
                      </Row>

                      <Row>
                        <Column>
                          <Input
                            name="email"
                            label="E-post"
                            isRequired={true}
                            value={values.email}
                            error={errors.email}
                            touched={touched.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Column>

                        <Column>
                          <Input
                            name="phone"
                            label="Telefon"
                            isRequired={true}
                            value={values.phone}
                            error={errors.phone}
                            touched={touched.phone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Column>
                      </Row>

                      <Row>
                        <Column>
                          <Input
                            name="invoiceNumber"
                            label="Fakturanummer"
                            isRequired={true}
                            value={values.invoiceNumber}
                            error={errors.invoiceNumber}
                            touched={touched.invoiceNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Column>
                      </Row>

                      <Row>
                        <Column>
                          <RadioButtonGroup
                            legend="Årsak til retur?"
                            value={values.cause}
                            error={errors.cause}
                            touched={touched.cause}
                          >
                            <RadioButton
                              name="cause"
                              label="Feil bestilt"
                              value="Wrong order"
                              checked={values.cause === 'Wrong order'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />

                            <RadioButton
                              name="cause"
                              label="Feil levert"
                              value="Wrong delivery"
                              checked={values.cause === 'Wrong delivery'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />

                            <RadioButton
                              name="cause"
                              label="Defekt"
                              value="Malfunction"
                              checked={values.cause === 'Malfunction'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </RadioButtonGroup>
                        </Column>
                      </Row>

                      <LoadingButton type="submit" loading={isSubmitting}>
                        Neste »
                  </LoadingButton>
                    </FieldSet>
                  )}

                  {step === 2 && (
                    <FieldSet disabled={isSubmitting}>
                      <Row>
                        <Column>
                          <Textarea
                            name="reason"
                            label="Hvorfor ønsker du å returnere varen?"
                            isRequired={true}
                            value={values.reason}
                            error={errors.reason}
                            touched={touched.reason}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Column>
                      </Row>

                      <Row>
                        <Column>
                          <RadioButtonGroup
                            legend="Hvor ble varen kjøpt?"
                            isRequired={true}
                            error={errors.where}
                            touched={touched.where}
                          >
                            <RadioButton
                              name="where"
                              label="Nettbutikk"
                              value="Online"
                              checked={values.where === 'Online'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />

                            <RadioButton
                              name="where"
                              label="Over disk"
                              value="Shop"
                              checked={values.where === 'Shop'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />

                            <RadioButton
                              name="where"
                              label="Kontakt pr tel./mail"
                              value="Mail"
                              checked={values.where === 'Mail'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </RadioButtonGroup>
                        </Column>
                      </Row>

                      <Row>
                        <Column>
                          <Textarea
                            name="measures"
                            label="Hva mener du kunne vært gjort for å unngå denne retur/reklamasjonen?"
                            value={values.measures}
                            error={errors.measures}
                            touched={touched.measures}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Column>
                      </Row>

                      <Row>
                        <Column>
                          <PrivacyPolicyCheckbox
                            name="accepted"
                            value={values.accepted}
                            checked={values.accepted}
                            error={errors.accepted}
                            touched={touched.accepted}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Column>
                      </Row>

                      <LoadingButton type="submit" loading={isSubmitting}>
                        Send inn
                      </LoadingButton>

                      <BackButton
                        type="button"
                        onClick={() => this.setState({ step: 1 })}
                      >
                        Gå tilbake
                      </BackButton>
                    </FieldSet>
                  )}
                </Form>}
              </div>}
          </Formik>}
        </Mutation>
      </StyledComplaintsForm>
    );
  }
}
